import type { GenericDateFilter } from 'v5/api/_common/query/filters/GenericDateFilter';
import type { ReportEntityType } from 'v5/api/report-templates/reportTemplates.api';
import type { ReportDateValue } from 'v5/core/reporting/reportTemplateQuery';
import { dateConvert } from 'v5/platform/lang/dateConvert';

/** The backend's `RelativeDates` the v1 editor offered, in the order this one offers them; a stored other one stays as it is. */
export const REPORT_RELATIVE_DATES = [
  'TODAY',
  'YESTERDAY',
  'TOMORROW',
  'LAST_7',
  'LAST_30',
  'NEXT_7',
  'NEXT_30',
  'LAST_WEEK',
  'NEXT_WEEK',
  'LAST_MONTH',
  'NEXT_MONTH',
  'QUARTER',
  'LAST_QUARTER',
  'NEXT_QUARTER'
] as const;

type ReportRelativeDate = (typeof REPORT_RELATIVE_DATES)[number];

export const DEFAULT_REPORT_RELATIVE_DATE: ReportRelativeDate = 'LAST_30';

export function isKnownRelativeDate(
  relative: string
): relative is ReportRelativeDate {
  return REPORT_RELATIVE_DATES.some(known => known === relative);
}

/** A preset is a backend relative value; a stored one this client doesn't know stays a preset too. */
export type ReportDateFilter = GenericDateFilter<string>;

/** The date fields the export filters on (`DataExportDateFilters`): creation, and the entity's own date. */
export function reportDateFieldsOf(entityType: ReportEntityType): string[] {
  switch (entityType) {
    case 'booking':
      return ['created_at', 'booking_date'];
    case 'production':
      return ['created_at', 'production_date'];
    case 'callsheet':
      return ['created_at', 'callsheet_date'];
    case 'contact':
    case 'callout':
    case 'shortlist':
      return ['created_at'];
  }
}

/** The export reads the date of the first 10 characters (`moment.utc(…).startOf('day')`). */
const calendarDateOf = (value: string | null) =>
  value === null ? null : dateConvert.calendarDate.from(value.slice(0, 10));

/** `null` when a stored range lacks a readable start or end: the chip can't show it. */
export function reportDateFilterOf(
  date: Exclude<ReportDateValue, null>
): ReportDateFilter | null {
  switch (date.kind) {
    case 'relative':
      return { operator: 'preset', value: date.relative };
    case 'range': {
      const from = calendarDateOf(date.start);
      const to = calendarDateOf(date.end);
      if (from === null || to === null) return null;
      return from.compare(to) === 0
        ? { operator: 'equal', value: from }
        : { operator: 'between', value: { from, to } };
    }
  }
}

/** Throws for "after" / "before": a stored range needs both ends, so the editor doesn't offer them. */
export function reportDateValueOf(
  field: string,
  filter: ReportDateFilter
): Exclude<ReportDateValue, null> {
  switch (filter.operator) {
    case 'preset':
      return { field, kind: 'relative', relative: filter.value };
    case 'equal':
      return {
        field,
        kind: 'range',
        start: filter.value.toString(),
        end: filter.value.toString()
      };
    case 'between':
      return {
        field,
        kind: 'range',
        start: filter.value.from.toString(),
        end: filter.value.to.toString()
      };
    case 'greaterThan':
    case 'lessThan':
      throw new Error(`No stored shape for a report date "${filter.operator}"`);
  }
}
